import * as React from 'react';

/**
 * Holds the byte-level upload percentage for a single in-flight upload.
 *
 * `apiClient.uploadFormData(url, formData, onProgress)` already emits a 0-100
 * percentage from the browser's `xhr.upload` progress events; this hook just
 * owns the state and the callback the mutation forwards down to it.
 *
 * Usage:
 *   const { progress, onProgress, reset } = useUploadProgress();
 *   mutate({ ...vars, onProgress }, { onSettled: reset });
 *   return <UploadProgress value={progress} />;
 *
 * `progress` is `null` while idle (nothing rendered) and a number once the
 * first progress event fires.
 */
function useUploadProgress() {
  const [progress, setProgress] = React.useState<number | null>(null);

  const onProgress = React.useCallback((value: number) => {
    setProgress(value);
  }, []);

  const reset = React.useCallback(() => {
    setProgress(null);
  }, []);

  return { progress, onProgress, reset };
}

export { useUploadProgress };
